import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Mail, Phone, MessageCircle, RefreshCcw } from "lucide-react";
import { api } from "@/lib/api";
import { DashboardHeader } from "@/components/DashboardHeader";
import { waLink } from "@/lib/site";

export const AdminDashboard = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q) return enquiries;
    const s = q.toLowerCase();
    return enquiries.filter(e =>
      [e.name, e.email, e.phone, e.postcode, e.moving_from, e.moving_to, e.services, e.message]
        .some(v => (v || "").toLowerCase().includes(s))
    );
  }, [enquiries, q]);

  const stats = useMemo(() => {
    const today = new Date().toDateString();
    const week = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return {
      total: enquiries.length,
      today: enquiries.filter(e => new Date(e.created_at).toDateString() === today).length,
      week: enquiries.filter(e => new Date(e.created_at).getTime() >= week).length,
      emailed: enquiries.filter(e => e.email_sent).length,
    };
  }, [enquiries]);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8FAFC]">
      <DashboardHeader title="Enquiries" subtitle="All quote requests received from boxb.uk." />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 pb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total" value={stats.total} testid="stat-total" />
          <StatCard label="Today" value={stats.today} testid="stat-today" />
          <StatCard label="Last 7 days" value={stats.week} testid="stat-week" />
          <StatCard label="Emails sent" value={stats.emailed} testid="stat-emailed" />
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, postcode, service…"
              className="w-full rounded-full border border-slate-200 bg-white pl-10 pr-4 py-3.5 text-[#0A192F] placeholder:text-slate-400 focus:border-[#0A192F] focus:ring-2 focus:ring-[#0A192F]/10 outline-none"
            />
          </div>
          <button data-testid="admin-refresh" onClick={load}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 font-semibold text-[#0A192F] hover:bg-slate-50">
            <RefreshCcw className="h-4 w-4" /> Refresh
          </button>
        </div>

        <div className="mt-6 rounded-3xl bg-white border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div data-testid="admin-empty" className="p-12 text-center text-slate-500">No enquiries match your search.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((e) => (
                <li key={e.id} data-testid={`admin-enquiry-${e.id}`} className="p-5 sm:p-6 grid md:grid-cols-12 gap-4 items-start">
                  <div className="md:col-span-4 min-w-0">
                    <div className="font-display font-semibold text-[#0A192F] truncate">{e.name}</div>
                    <div className="mt-1 text-sm text-slate-500">
                      {new Date(e.created_at).toLocaleString("en-GB")}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-medium px-3 py-1.5">
                        <Mail className="h-3.5 w-3.5" /> {e.email}
                      </a>
                      <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-medium px-3 py-1.5">
                        <Phone className="h-3.5 w-3.5" /> {e.phone}
                      </a>
                      <a href={waLink(`Hi ${e.name}, thanks for your BoxB enquiry.`)} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1 rounded-full bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#0A5C2E] text-xs font-medium px-3 py-1.5">
                        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                      </a>
                    </div>
                  </div>
                  <div className="md:col-span-5">
                    <div className="text-xs tracking-widest uppercase font-bold text-slate-500">Move</div>
                    <div className="mt-1 text-sm text-[#0A192F] font-medium">
                      {e.moving_from || "—"} <span className="text-slate-400 mx-1">→</span> {e.moving_to || "—"}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                      <Kv k="Postcode" v={e.postcode} />
                      <Kv k="Date" v={e.preferred_date} />
                      <Kv k="Service" v={e.services} />
                      <Kv k="Email sent" v={e.email_sent ? "Yes" : "No"} />
                    </div>
                    {e.message && (
                      <p className="mt-3 text-sm text-slate-700 bg-slate-50 rounded-2xl p-3 border border-slate-100">
                        {e.message}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <span className="inline-flex items-center rounded-full bg-[#0A192F] text-white px-3 py-1.5 text-xs font-semibold">
                      {e.services || "Quote request"}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="rounded-3xl bg-white border border-slate-200 p-6">
    <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">{label}</div>
    <div className="mt-2 font-display font-bold text-3xl text-[#0A192F]">{value}</div>
  </div>
);

const Kv = ({ k, v }) => (
  <div>
    <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">{k}</div>
    <div className="text-sm text-[#0A192F]">{v || "—"}</div>
  </div>
);
